import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import { CSSProperties } from "@material-ui/styles";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { ListActions, ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage } from "react-intl";

import { formatCurrencyData, formatDate } from "../../../utils/misc";
import { MerchantSettlement_merchantSettlement_edges_node } from "../../types/MerchantSettlement";

const useStyles = makeStyles(
  theme => {
    const overflowing: CSSProperties = {
      overflow: "hidden",
      textOverflow: "ellipsis"
    };
    return {
      colMedium: {
        overflowing,
        width: "12em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colNarrow: {
        overflowing,
        width: "8em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colRow: {
        "& th": {
          fontWeight: 600 as 600,
          paddingBottom: theme.spacing(1),
          paddingTop: theme.spacing(1)
        }
      },
      colWide: {
        overflowing,
        width: "16em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      table: {
        tableLayout: "fixed"
      }
    };
  },
  { name: "OrderLinesFinanceList" }
);

interface MerchantSettlementListProp extends ListProps, ListActions {
  settlements: MerchantSettlement_merchantSettlement_edges_node[];
}

const MerchantSettlementList: React.FC<MerchantSettlementListProp> = props => {
  const {
    settings,
    disabled,
    settlements,
    pageInfo,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    onRowClick,
    // onSort,
    toolbar,
    toggle,
    toggleAll,
    selected,
    // sort,
    isChecked
  } = props;

  const colNum = 6;
  const classes = useStyles(props);
  return (
    <>
      <ResponsiveTable className={classes.table}>
        <TableHead
          colSpan={colNum}
          selected={selected}
          disabled={disabled}
          items={settlements}
          toggleAll={toggleAll}
          toolbar={toolbar}
        >
          <TableCellHeader className={classes.colMedium}>
            <FormattedMessage defaultMessage="Payout ID" />
          </TableCellHeader>
          <TableCellHeader className={classes.colMedium}>
            <FormattedMessage defaultMessage="Mall" />
          </TableCellHeader>
          <TableCellHeader className={classes.colWide}>
            <FormattedMessage defaultMessage="Covered Period" />
          </TableCellHeader>

          <TableCellHeader className={classes.colWide}>
            <FormattedMessage defaultMessage="Settlement Date" />
          </TableCellHeader>

          <TableCellHeader className={classes.colMedium}>
            <FormattedMessage defaultMessage="Net Settlement" />
          </TableCellHeader>
        </TableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={colNum}
              settings={settings}
              hasNextPage={
                pageInfo && !disabled ? pageInfo.hasNextPage : undefined
              }
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : undefined
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
        <TableBody>
          {settlements &&
            renderCollection(
              settlements,
              (settlement, index) => {
                const isSelected = settlement
                  ? isChecked(settlement.id)
                  : false;

                return (
                  <TableRow
                    hover={!!settlement}
                    key={settlement ? index : "skeleton"}
                    onClick={() => onRowClick}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        disabled={disabled}
                        disableClickPropagation
                        onChange={() => toggle(settlement.id)}
                      />
                    </TableCell>
                    <TableCell>
                      {maybe<React.ReactNode>(
                        () => atob(settlement.id),
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>
                      {maybe<React.ReactNode>(
                        () => settlement.mall_name,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>
                      {maybe<React.ReactNode>(
                        () =>
                          `${formatDate(
                            settlement.coverage_start_date
                          )} - ${formatDate(settlement.coverage_end_date)}`,
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>
                      {maybe<React.ReactNode>(
                        () => formatDate(settlement.settlement_date),
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>
                      {maybe<React.ReactNode>(
                        () =>
                          `P ${formatCurrencyData(settlement.net_settlement)}`,
                        <Skeleton />
                      )}
                    </TableCell>
                  </TableRow>
                );
              },
              () => (
                <TableRow>
                  <TableCell colSpan={colNum}>
                    <FormattedMessage defaultMessage="No settlements found" />
                  </TableCell>
                </TableRow>
              )
            )}
        </TableBody>
      </ResponsiveTable>
    </>
  );
};

export default MerchantSettlementList;
